<script lang="ts">
  import ToolbarIcon, { type ToolIconName } from './ToolbarIcon.svelte';
  import Icon from './Icon.svelte';
  import { t } from '../i18n/index.svelte';
  import type { RenderMode } from '../core/SceneManager';

  let {
    renderMode,
    hasAnimatable,
    measureActive,
    lightsOn,
    hasDims,
    dimsOn,
    onRenderMode,
    onView,
    onOpenAll,
    onCloseAll,
    onMeasure,
    onToggleLights,
    onToggleDims,
  }: {
    renderMode: RenderMode;
    hasAnimatable: boolean;
    measureActive: boolean;
    lightsOn: boolean;
    hasDims: boolean;
    dimsOn: boolean;
    onRenderMode: (m: RenderMode) => void;
    onView: (p: 'iso' | 'front' | 'left' | 'right' | 'top') => void;
    onOpenAll: () => void;
    onCloseAll: () => void;
    onMeasure: () => void;
    onToggleLights: () => void;
    onToggleDims: () => void;
  } = $props();

  const RENDER_MODES: { mode: RenderMode; icon: ToolIconName; key: string }[] = [
    { mode: 'material', icon: 'material', key: 'tb.material' },
    { mode: 'matedge', icon: 'matedge', key: 'tb.matedge' },
    { mode: 'projection', icon: 'projection', key: 'tb.projection' },
    { mode: 'xray', icon: 'xray', key: 'tb.xray' },
    { mode: 'wireframe', icon: 'wireframe', key: 'tb.wireframe' },
    { mode: 'wireframe-hidden', icon: 'wireframe-hidden', key: 'tb.wireframeHidden' },
  ];

  const VIEWS: { p: 'iso' | 'front' | 'left' | 'right' | 'top'; icon: ToolIconName; key: string }[] = [
    { p: 'iso', icon: 'cam-perspective', key: 'tb.viewIso' },
    { p: 'front', icon: 'cam-front', key: 'tb.viewFront' },
    { p: 'left', icon: 'cam-left', key: 'tb.viewLeft' },
    { p: 'right', icon: 'cam-right', key: 'tb.viewRight' },
    { p: 'top', icon: 'cam-top', key: 'tb.viewTop' },
  ];
</script>

<div class="toolbar" role="toolbar" aria-label={t('tb.toolbar')}>
  <!-- HÀNG 1: chỉ icon (chế độ hiển thị + góc nhìn) -->
  <div class="trow">
    <div class="group" role="group" aria-label={t('tb.modeGroup')}>
      {#each RENDER_MODES as m (m.mode)}
        <button
          class:active={renderMode === m.mode}
          title={t(m.key)}
          aria-label={t(m.key)}
          aria-pressed={renderMode === m.mode}
          onclick={() => onRenderMode(m.mode)}
        >
          <ToolbarIcon name={m.icon} size={18} />
        </button>
      {/each}
    </div>
    <span class="divider"></span>
    <div class="group" role="group" aria-label={t('tb.viewGroup')}>
      {#each VIEWS as v (v.p)}
        <button title={t(v.key)} aria-label={t(v.key)} onclick={() => onView(v.p)}>
          <ToolbarIcon name={v.icon} size={18} />
        </button>
      {/each}
    </div>
  </div>

  <!-- HÀNG 2 (desktop): nhóm có nhãn (Mở/Đóng/Đo). Mobile: gộp vào 1 hàng cuộn (display:contents). -->
  <div class="trow">
    <span class="divider sep"></span>
    {#if hasAnimatable}
      <div class="group" role="group" aria-label={t('author.catDoor')}>
        <button class="labeled" title={t('tb.openAll')} onclick={onOpenAll}>
          <ToolbarIcon name="open-all" size={18} /><span>{t('tb.openAll')}</span>
        </button>
        <button class="labeled" title={t('tb.closeAll')} onclick={onCloseAll}>
          <ToolbarIcon name="close-all" size={18} /><span>{t('tb.closeAll')}</span>
        </button>
      </div>
      <span class="divider"></span>
    {/if}
    <div class="group" role="group" aria-label={t('measure.title')}>
      <button class="labeled" class:active={measureActive} title={t('measure.title')} onclick={onMeasure}>
        <ToolbarIcon name="measure" size={18} /><span>{t('tb.measure')}</span>
      </button>
    </div>
    <!-- Kích thước user dựng (04b): chỉ hiện khi model có dimensions. Toggle độc lập, mặc định tắt. -->
    {#if hasDims}
      <span class="divider"></span>
      <div class="group">
        <button
          class="labeled"
          class:active={dimsOn}
          title={t('tb.dimensions')}
          aria-label={t('tb.dimensions')}
          aria-pressed={dimsOn}
          onclick={onToggleDims}
        >
          <Icon name="dimension" size={18} /><span>{t('tb.dimensions')}</span>
        </button>
      </div>
    {/if}
    <span class="divider"></span>
    <!-- Chiếu sáng: công tắc cho NGƯỜI XEM (mặc định tắt → mẫu phẳng, không bóng). Bật = preset tác giả. -->
    <div class="group">
      <button
        class="labeled"
        class:active={lightsOn}
        title={t('tb.lighting')}
        aria-label={t('tb.lighting')}
        aria-pressed={lightsOn}
        onclick={onToggleLights}
      >
        <Icon name="sun" size={18} /><span>{t('tb.lighting')}</span>
      </button>
    </div>
  </div>
</div>

<style>
  .toolbar {
    position: absolute;
    /* left:0/right:0 + margin auto + fit-content: co theo nội dung & căn giữa mà KHÔNG bị
       giới hạn 50% viewport (left:50% trước đây khiến hàng icon bị wrap). */
    left: 0;
    right: 0;
    bottom: var(--space-md);
    margin-inline: auto;
    width: fit-content;
    z-index: var(--z-toolbar);
    display: flex;
    flex-direction: column; /* desktop: 2 hàng — icon (trên), nhãn Mở/Đóng/Đo (dưới) */
    align-items: center;
    gap: 2px;
    max-width: calc(100vw - 2 * var(--space-md));
    padding: 6px 12px;
    background: var(--color-surface-glass);
    border: 1px solid var(--color-border);
    border-radius: 22px; /* bo chữ nhật cho khối 2 hàng (không pill → end-icon không lòi góc) */
    box-shadow: var(--shadow-panel);
    backdrop-filter: blur(10px);
  }
  /* mỗi hàng: tự co theo nội dung, căn giữa → toolbar rộng = hàng rộng nhất (cân đối) */
  .trow {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-xs);
    flex-wrap: wrap;
  }
  .sep {
    display: none; /* divider giữa view↔Mở chỉ hiện khi gộp 1 hàng (mobile) */
  }
  .group {
    display: flex;
    align-items: center;
    gap: 2px;
  }
  .divider {
    width: 1px;
    align-self: stretch;
    margin: 4px 2px;
    background: var(--color-border);
  }
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-width: var(--size-touch-min);
    height: var(--size-touch-min);
    padding: 0 10px;
    border: none;
    border-radius: var(--radius-pill);
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: background var(--motion-base) var(--motion-easing), color var(--motion-base) var(--motion-easing);
  }
  button span {
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 600;
    white-space: nowrap;
  }
  button:hover {
    background: var(--color-canvas-bottom);
    color: var(--color-primary);
  }
  /* icon raster nhiều màu KHÔNG nhuộm trắng được → active = nền xanh NHẠT + viền xanh,
     icon giữ nguyên màu gốc (chữ nút có nhãn chuyển xanh primary). */
  button.active {
    background: color-mix(in srgb, var(--color-primary) 14%, transparent);
    box-shadow: inset 0 0 0 1.5px var(--color-primary);
    color: var(--color-primary);
  }
  .labeled {
    padding: 0 var(--space-sm);
  }

  @media (max-width: 767px) {
    .toolbar {
      bottom: 76px; /* chừa chỗ cho bottom-sheet thu gọn */
      width: auto; /* mobile: trải gần đủ bề ngang rồi cuộn trong (không fit-content) */
      flex-direction: row; /* mobile: gộp lại 1 hàng cuộn ngang */
      flex-wrap: nowrap;
      align-items: center;
      gap: var(--space-xs);
      overflow-x: auto;
      justify-content: flex-start;
      scrollbar-width: none;
      max-width: calc(100vw - 2 * var(--space-sm));
      border-radius: var(--radius-pill);
      padding: 6px;
    }
    .toolbar::-webkit-scrollbar {
      display: none;
    }
    .trow {
      display: contents; /* phẳng hoá → mọi nhóm/divider chảy vào 1 hàng cuộn */
    }
    .sep {
      display: block; /* khôi phục divider giữa view↔Mở khi gộp 1 hàng */
    }
    .group {
      flex: none;
    }
    .labeled span {
      display: none;
    }
  }
</style>
